import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, filter, map, merge, of, shareReplay, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { SurveyService } from '@ps/survey-data-access';
import { SurveyKind } from '@ps/survey-utils';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from './survey-creation-core.actions';
import {
  selectActiveChildKey,
  selectCoreChildren,
  selectRootSurvey,
} from './survey-creation-core.selectors';
import { childSurveysRequest } from './utils/child-survey-filter.helper';
import {
  activeProjectChildKey,
  isLoadedCoreSibling,
  resolveDisplayedActiveChildKey,
  rootSurveyOf,
} from './utils/core-children.helper';
import { loadActiveChild$ } from './utils/active-child-load.helper';
import { surveyCreationFeature } from '../survey-creation.feature';
import { SurveyCreationAudienceActions } from '../audience/survey-creation-audience.actions';
import { SurveyCreationLaunchActions } from '../launch/survey-creation-launch.actions';

@Injectable()
export class SurveyCreationCoreEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _surveyService = inject(SurveyService);

  // Cross-listens to both stage-specific init actions — the survey has to
  // load regardless of which page opened it. See
  // SurveyCreationAudienceActions.init's doc comment.
  public readonly loadRootSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.init,
        SurveyCreationLaunchActions.init,
      ),
      filter(
        (action): action is typeof action & { surveyId: number } =>
          !!action.surveyId,
      ),
      concatLatestFrom(() => [this._store.select(selectRootSurvey)]),
      // Idempotence guard: skip the fetch when this exact survey is already in
      // memory. Compared against the root, not the active child: a project's
      // active child is a different survey than the id `init` carries.
      filter(
        ([{ surveyId }, rootSurvey]) =>
          !rootSurvey || rootSurvey.id !== surveyId,
      ),
      switchMap(([action]) =>
        this._surveyService.getByIdTargeting(action.surveyId).pipe(
          map((survey) =>
            SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess({
              survey,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationCoreApiActions.loadRootSurveyForEditFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // A standard/mc survey is its own active child, so its already-loaded data
  // is reused directly. A project's active child is a different survey than
  // the root just loaded above, so its own targeting data is fetched too,
  // the same way the root's was.
  public readonly loadActiveSurveyForEdit$ = createEffect(() => {
    const trigger$ = this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      concatLatestFrom(() => [
        this._store.select(selectActiveChildKey),
        this._store.select(selectCoreChildren),
      ]),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const rootLoaded$ = trigger$.pipe(
      filter(
        ([{ survey }, , children]) =>
          childSurveysRequest(survey).isNone() ||
          isLoadedCoreSibling(children, survey.id),
      ),
      map(([{ survey }, activeChildKey]) =>
        activeProjectChildKey(survey, activeChildKey),
      ),
    );

    const familyLoaded$ = this._actions$.pipe(
      ofType(SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess),
      concatLatestFrom(() =>
        this._store.select(surveyCreationFeature.selectCore),
      ),
      map(([{ surveys }, core]) =>
        rootSurveyOf(core).andThen((survey) =>
          activeProjectChildKey(
            survey,
            resolveDisplayedActiveChildKey(core, surveys),
          ),
        ),
      ),
    );

    const familyFailed$ = this._actions$.pipe(
      ofType(SurveyCreationCoreChildrenApiActions.loadChildSurveysFailure),
      concatLatestFrom(() =>
        this._store.select(surveyCreationFeature.selectCore),
      ),
      map(([, core]) =>
        rootSurveyOf(core).andThen((survey) =>
          activeProjectChildKey(survey, core.activeChildKey),
        ),
      ),
    );

    return merge(
      trigger$.pipe(
        filter(([{ survey }]) => !SurveyKind.isProject(survey)),
        map(([{ survey }]) =>
          SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess({
            survey,
          }),
        ),
      ),
      merge(rootLoaded$, familyFailed$).pipe(
        switchMap((activeChildKey) =>
          activeChildKey.match({
            some: (key) =>
              loadActiveChild$(
                this._surveyService.getByIdTargeting(key, true),
                this.psCatchError,
              ),
            none: () => EMPTY,
          }),
        ),
      ),
      familyLoaded$.pipe(
        switchMap((activeChildKey) =>
          activeChildKey.match({
            some: (key) =>
              loadActiveChild$(
                this._surveyService.getByIdTargeting(key, true),
                this.psCatchError,
                {
                  precedingActions: [
                    SurveyCreationCoreActions.setActiveChildKey({
                      activeChildKey: key,
                    }),
                  ],
                },
              ),
            none: () => EMPTY,
          }),
        ),
      ),
    );
  });
}
